import Vue from 'vue';
import VueRouter from 'vue-router';
import { authGuard } from '../auth/authGuard';
import Dashboard from '../views/Dashboard.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'dashboard',
    meta: { title: 'Dashboard' },
    component: Dashboard
  },
  {
    path: '/catalog',
    name: 'catalog',
    meta: { title: 'Catalog' },
    component: () => import(/* webpackChunkName: "catalog" */ '../views/catalog/Index.vue')
  },
  {
    path: '/catalog/:id',
    name: 'catalogDetail',
    meta: { title: 'Catalog Detail' },
    component: () => import(/* webpackChunkName: "catalog" */ '../views/catalog/Detail.vue')
  },
  {
    path: '/categories/:id',
    name: 'categoryDetail',
    meta: { title: 'Category Detail' },
    component: () => import(/* webpackChunkName: "category" */ '../views/categories/Detail.vue')
  },
  {
    path: '/products/:id',
    name: 'productDetail',
    meta: { title: 'Core Product Detail' },
    component: () => import(/* webpackChunkName: "products" */ '../views/products/Detail.vue')
  },
  {
    path: '/designgroups/:id',
    name: 'designGroupDetail',
    meta: { title: 'Design Group Detail' },
    component: () =>
      import(/* webpackChunkName: "designgroup" */ '../views/designgroups/Detail.vue')
  },
  {
    path: '/topics/:id',
    name: 'topicDetail',
    meta: { title: 'Topic Detail' },
    component: () => import(/* webpackChunkName: "topic" */ '../views/topics/Detail.vue')
  },
  {
    path: '/industries/:id',
    name: 'industryDetail',
    meta: { title: 'Industry Detail' },
    component: () => import(/* webpackChunkName: "industry" */ '../views/industries/Detail.vue')
  },
  {
    path: '/guides',
    name: 'guides',
    meta: { title: 'Guides' },
    component: () => import(/* webpackChunkName: "guides" */ '../views/guides/Index.vue')
  },
  {
    path: '/outputs',
    name: 'outputs',
    meta: { title: 'Outputs' },
    component: () => import(/* webpackChunkName: "outputs" */ '../views/outputs/Index.vue')
  },
  {
    path: '/users',
    name: 'usersIndex',
    meta: { title: 'Users' },
    component: () => import(/* webpackChunkName: "users" */ '../views/users/Index.vue')
  },
  {
    path: '/users/contributors',
    name: 'contributorsIndex',
    meta: { title: 'Contributors' },
    component: () => import(/* webpackChunkName: "users" */ '../views/users/Index.vue')
  },
  {
    path: '/users/designers',
    name: 'designersIndex',
    meta: { title: 'Designers' },
    component: () => import(/* webpackChunkName: "users" */ '../views/users/Index.vue')
  },
  {
    path: '/users/:id',
    name: 'usersDetail',
    meta: { title: 'Users' },
    component: () => import(/* webpackChunkName: "users" */ '../views/users/Detail.vue')
  },
  {
    path: '/print',
    name: 'print',
    meta: { title: 'Print' },
    component: () => import(/* webpackChunkName: "print" */ '../views/print/Index.vue')
  },
  {
    path: '/promo',
    name: 'promo',
    meta: { title: 'Promo' },
    component: () => import(/* webpackChunkName: "promo" */ '../views/promo/Index.vue')
  },
  {
    path: '/marketing',
    name: 'marketingDashboard',
    meta: { title: 'Marketing' },
    component: () => import(/* webpackChunkName: "marketing" */ '../views/marketing/Index.vue')
  },
  {
    path: '/marketing/campaigns',
    name: 'campaigns',
    meta: { title: 'Campaigns' },
    component: () =>
      import(/* webpackChunkName: "campaigns" */ '../views/marketing/campaigns/Index.vue')
  },
  {
    path: '/marketing/campaigns/:id',
    name: 'campaignDetail',
    meta: { title: 'Campaign' },
    component: () =>
      import(/* webpackChunkName: "campaigns" */ '../views/marketing/campaigns/Detail.vue')
  },
  {
    // "Retargeting", not "Ad Audiences" — "Audiences" already means Industry/Topic content
    // planning in this app, and reusing the word for ad targeting would collide with it.
    path: '/marketing/retargeting',
    name: 'retargeting',
    meta: { title: 'Retargeting' },
    component: () =>
      import(/* webpackChunkName: "retargeting" */ '../views/marketing/retargeting/Index.vue')
  },
  {
    path: '/marketing/audiences',
    name: 'audiences',
    meta: { title: 'Audiences' },
    component: () =>
      import(/* webpackChunkName: "audiences" */ '../views/marketing/audiences/Index.vue')
  },
  {
    path: '/marketing/audiences/:id',
    name: 'audienceDetail',
    meta: { title: 'Audience' },
    component: () =>
      import(/* webpackChunkName: "audiences" */ '../views/marketing/audiences/Detail.vue')
  },
  {
    path: '/marketing/subjects',
    name: 'subjectPerformance',
    meta: { title: 'Subject lines' },
    component: () =>
      import(/* webpackChunkName: "subjects" */ '../views/marketing/subjects/Index.vue')
  },
  // Legacy path — keep for one release so bookmarks survive, then delete. (/marketing itself is no longer a
  // redirect: it is the dashboard, and the Marketing nav group lands there.)
  { path: '/marketing/:id(\\d+)', redirect: (to) => `/marketing/campaigns/${to.params.id}` },
  {
    path: '/reports/campaigns',
    name: 'reportsCampaigns',
    meta: { title: 'Ad Campaigns' },
    component: () => import(/* webpackChunkName: "reports" */ '../views/reports/Campaigns.vue')
  },
  {
    path: '/reports/ads',
    name: 'reportsAds',
    meta: { title: 'Ads' },
    component: () => import(/* webpackChunkName: "reports" */ '../views/reports/Ads.vue')
  },
  {
    path: '/print/:id',
    name: 'printDetail',
    meta: { title: 'Print Detail' },
    component: () => import(/* webpackChunkName: "print" */ '../views/print/Detail.vue')
  },
  {
    path: '/print/products/:id',
    name: 'printProductDetail',
    meta: { title: 'Product Detail' },
    component: () => import(/* webpackChunkName: "print" */ '../views/print/ProductDetail.vue')
  },
  {
    path: '/promo/:id',
    name: 'promoDetail',
    meta: { title: 'Promo Detail' },
    component: () => import(/* webpackChunkName: "print" */ '../views/promo/Detail.vue')
  },
  {
    path: '/promo/products/:id',
    name: 'promoProductDetail',
    meta: { title: 'Promo Product Detail' },
    component: () => import(/* webpackChunkName: "print" */ '../views/promo/ProductDetail.vue')
  },
  {
    path: '/orders/queue',
    name: 'orderQueue',
    meta: { title: 'Print Orders' },
    component: () => import(/* webpackChunkName: "orders" */ '../views/orders/Queue.vue')
  },
  {
    path: '/orders/completed',
    name: 'orderCompleted',
    meta: { title: 'Completed Print Orders' },
    component: () => import(/* webpackChunkName: "orders" */ '../views/orders/Completed.vue')
  },
  {
    path: '/orders/:id',
    name: 'orderDetail',
    meta: { title: 'Order Details' },
    component: () => import(/* webpackChunkName: "orders" */ '../views/orders/Detail.vue')
  },
  {
    path: '/reviews',
    name: 'reviews',
    meta: { title: 'Reviews' },
    component: () => import(/* webpackChunkName: "products" */ '../views/reviews/Index.vue')
  },
  {
    path: '/promocodes',
    name: 'promocodes',
    meta: { title: 'Promo Codes' },
    component: () => import(/* webpackChunkName: "products" */ '../views/promocodes/Index.vue')
  },
  {
    path: '/content',
    name: 'content',
    meta: { title: 'Content Detail' },
    component: () => import(/* webpackChunkName: "content" */ '../views/content/Detail.vue')
  },
  {
    path: '/content/:id',
    name: 'contentDetail',
    meta: { title: 'Content Detail' },
    component: () => import(/* webpackChunkName: "content" */ '../views/content/Detail.vue')
  },
  {
    path: '/tasks',
    name: 'tasks',
    meta: { title: 'Content Detail' },
    component: () => import(/* webpackChunkName: "tasks" */ '../views/tasks/Detail.vue')
  },
  {
    path: '/tasks/:id',
    name: 'tasksDetail',
    meta: { title: 'Content Detail' },
    component: () => import(/* webpackChunkName: "tasks" */ '../views/tasks/Detail.vue')
  }
];

const router = new VueRouter({
  routes,
  mode: 'history',
  base: '/',
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

router.beforeEach((to, from, next) => {
  if (to.meta) {
    document.title = to.meta.title + ' | MCS Admin';
  }
  authGuard(to, from, next);
});

export default router;
